/**
 * Wiederverwendbare Hilfe-Komponenten:
 *  - <InfoTip>   inline ⓘ-Icon mit Hover-Tooltip oder Click-Popover (Mobile)
 *  - <HelpBox>   farbiger Hinweis-Block (info/warn/tip) für Section-Köpfe
 *  - <FieldHelp> kleine graue Hilfezeile unter einem Input
 *  - <Examples>  Beispiel-Pillen die per Klick in ein Feld geschrieben werden
 *
 * Ziel: Jeder Agent soll auch ohne Doku verstehen was ein Feld macht,
 * was passiert wenn er etwas ändert, und sehen wie ein Beispiel aussieht.
 */
import { useEffect, useRef, useState } from "react";
import { HelpCircle, Info, Lightbulb, AlertTriangle, X } from "lucide-react";

// ============================================================
// Inline ⓘ-Tooltip neben einem Label
// ============================================================

export function InfoTip({
  children,
  title,
  maxWidth = 260,
}: {
  children: React.ReactNode;
  title?: string;
  maxWidth?: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  // Outside-Click + ESC schließt das Popover (für Mobile)
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span ref={ref} className="relative inline-flex items-center align-middle">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="inline-grid place-items-center size-4 rounded-full text-ink-muted/70 hover:text-accent-orange transition-colors"
        aria-label={title ? `Hilfe: ${title}` : "Hilfe"}
      >
        <HelpCircle className="size-3.5" />
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute top-5 left-1/2 -translate-x-1/2 z-50 rounded-lg bg-ink text-white text-[11px] leading-relaxed px-3 py-2 shadow-xl pointer-events-none"
          style={{ width: maxWidth, maxWidth: "calc(100vw - 24px)" }}
        >
          {title && (
            <span className="block font-semibold mb-0.5">{title}</span>
          )}
          {children}
          {/* Pfeil */}
          <span
            className="absolute -top-1 left-1/2 -translate-x-1/2 size-2 rotate-45 bg-ink"
            aria-hidden="true"
          />
        </span>
      )}
    </span>
  );
}

// ============================================================
// HelpBox — farbiger Hinweis am Sektionskopf
// ============================================================

export function HelpBox({
  kind = "info",
  title,
  children,
  collapsible = false,
}: {
  kind?: "info" | "tip" | "warn";
  title?: string;
  children: React.ReactNode;
  collapsible?: boolean;
}) {
  const [open, setOpen] = useState(!collapsible);

  const palette =
    kind === "warn"
      ? { bg: "#fef3c7", border: "#fde68a", color: "#7a5d00", icon: <AlertTriangle className="size-4" /> }
      : kind === "tip"
        ? { bg: "#dcfce7", border: "#86efac", color: "#15803d", icon: <Lightbulb className="size-4" /> }
        : { bg: "#dbeafe", border: "#93c5fd", color: "#1e40af", icon: <Info className="size-4" /> };

  return (
    <div
      className="rounded-xl border px-3 py-2.5 text-xs leading-relaxed"
      style={{ background: palette.bg, borderColor: palette.border, color: palette.color }}
    >
      <button
        type="button"
        onClick={() => collapsible && setOpen((v) => !v)}
        className={`flex items-start gap-2 w-full text-left ${
          collapsible ? "cursor-pointer" : "cursor-default"
        }`}
      >
        <span className="flex-shrink-0 mt-0.5">{palette.icon}</span>
        <span className="flex-1">
          {title && <strong className="block">{title}</strong>}
          {open && <span className={title ? "block mt-0.5" : ""}>{children}</span>}
          {collapsible && !open && (
            <span className="block mt-0.5 opacity-70">Klick für Details</span>
          )}
        </span>
        {collapsible && (
          <span className="text-[10px] mt-1 opacity-60">{open ? "−" : "+"}</span>
        )}
      </button>
    </div>
  );
}

// ============================================================
// FieldHelp — kleine graue Erklärung unter einem Input
// ============================================================

export function FieldHelp({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-1 text-[11px] text-ink-muted leading-relaxed">{children}</p>
  );
}

// ============================================================
// Examples — Beispielpillen die per Klick ins Feld geschrieben werden
// ============================================================

export function Examples({
  items,
  onPick,
}: {
  items: string[];
  onPick: (value: string) => void;
}) {
  if (!items.length) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1">
      <span className="text-[10px] uppercase tracking-wider text-ink-muted/70 font-semibold mr-1 self-center">
        Beispiel:
      </span>
      {items.map((ex) => (
        <button
          key={ex}
          type="button"
          onClick={() => onPick(ex)}
          className="rounded-md border border-line bg-white/50 px-1.5 py-0.5 text-[10px] text-ink-muted hover:bg-cream-soft hover:text-ink transition"
          title="Klick zum Übernehmen"
        >
          {ex}
        </button>
      ))}
    </div>
  );
}

// ============================================================
// FirstSteps — Onboarding-Modal "Was muss ich als Erstes machen?"
// ============================================================

export function FirstStepsModal({
  steps,
  onClose,
}: {
  steps: { label: string; done: boolean; jumpTo?: string }[];
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="text-lg font-bold">Erste Schritte</h2>
            <p className="text-xs text-ink-muted mt-1">
              So machst du deinen Shop fertig — in wenigen Minuten.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 hover:bg-cream-soft"
            aria-label="Schließen"
          >
            <X className="size-4" />
          </button>
        </div>
        <ul className="space-y-2">
          {steps.map((s, i) => (
            <li
              key={i}
              className="flex items-start gap-3 rounded-lg border border-line p-2.5 text-sm"
            >
              <span
                className={`grid place-items-center size-6 rounded-full text-xs font-bold flex-shrink-0 ${
                  s.done
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-cream-soft text-ink-muted"
                }`}
              >
                {s.done ? "✓" : i + 1}
              </span>
              <span className={s.done ? "line-through text-ink-muted" : ""}>
                {s.label}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
